<script setup>
import { ref, onMounted, computed } from 'vue';
import { products } from '@/data/products';
import { Head } from '@inertiajs/vue3';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const props = defineProps({
    slug: String,
    project: Object
});

gsap.registerPlugin(ScrollTrigger)

// animations
const bentoGrid1StartAni = ref(null)
const bentoGrid2StartAni = ref(null)
const bentoGrid3StartAni = ref(null)
const bentoGrid4StartAni = ref(null)
const showcaseStartAni = ref(null)
const infoStartAni = ref(null);

const heroTitle = ref(null)
const heroSubtitle = ref(null)

const bentoGridInfotext1Ref = ref(null)
const bentoGridShowcase1Ref = ref(null)
const bentoGridInfotext2Ref = ref(null)
const bentoGridShowcase2Ref = ref(null)
const bentoGridInfotext3Ref = ref(null)
const bentoGridShowcase3Ref = ref(null)
const bentoGridInfotext4Ref = ref(null)
const bentoGridShowcase4Ref = ref(null)

const showcaseImages = ref(null)

const subDescription = ref(null);
const extraInfo = ref(null);
const infoImage = ref(null); // Add this new ref for the image

/**
 * Check if an element is in the viewport
 * @param {HTMLElement} element - The DOM element to check
 * @returns {boolean} - Whether the element is in the viewport
 */
const isInViewport = (element) => {
  if (!element) return false;
  
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

// Find product by slug from the URL
const product = computed(() => {
    return products.find(p => p.meta.slug === props.slug) || products[0];
});

// Generate dynamic color classes for the project
const projectColors = computed(() => {
    const colorCode = props.project?.project_code || 'gray';
    const mainColor = props.project?.main_color || '#6b7280'; // fallback to gray-500
    
    return {
        colorCode,
        mainColor,
        // Generate color palette based on main color
        generateColorPalette: (baseColor) => {
            // Convert hex to HSL for manipulation
            const hexToHsl = (hex) => {
                const r = parseInt(hex.slice(1, 3), 16) / 255;
                const g = parseInt(hex.slice(3, 5), 16) / 255;
                const b = parseInt(hex.slice(5, 7), 16) / 255;
                
                const max = Math.max(r, g, b);
                const min = Math.min(r, g, b);
                let h, s, l = (max + min) / 2;
                
                if (max === min) {
                    h = s = 0;
                } else {
                    const d = max - min;
                    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
                    switch (max) {
                        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                        case g: h = (b - r) / d + 2; break;
                        case b: h = (r - g) / d + 4; break;
                    }
                    h /= 6;
                }
                
                return [h * 360, s * 100, l * 100];
            };
            
            // Convert HSL back to hex
            const hslToHex = (h, s, l) => {
                h /= 360; s /= 100; l /= 100;
                const a = s * Math.min(l, 1 - l);
                const f = n => {
                    const k = (n + h / (1/12)) % 12;
                    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
                    return Math.round(255 * color).toString(16).padStart(2, '0');
                };
                return `#${f(0)}${f(8)}${f(4)}`;
            };
            
            const [h, s, l] = hexToHsl(baseColor);
            
            return {
                50: hslToHex(h, s, Math.min(95, l + 45)),
                100: hslToHex(h, s, Math.min(90, l + 35)),
                200: hslToHex(h, s, Math.min(85, l + 25)),
                300: hslToHex(h, s, Math.min(75, l + 15)),
                400: hslToHex(h, s, Math.min(65, l + 5)),
                500: baseColor, // main color
                600: hslToHex(h, s, Math.max(35, l - 5)),
                700: hslToHex(h, s, Math.max(25, l - 15)),
                800: hslToHex(h, s, Math.max(15, l - 25)),
                900: hslToHex(h, s, Math.max(10, l - 35)),
            };
        }
    };
});

// Generate CSS variables for the project colors
const projectColorStyles = computed(() => {
    const { colorCode, mainColor, generateColorPalette } = projectColors.value;
    const palette = generateColorPalette(mainColor);
    
    let css = ':root {\n';
    Object.entries(palette).forEach(([shade, color]) => {
        css += `  --color-${colorCode}-${shade}: ${color};\n`;
    });
    css += '}\n\n';
    
    // Generate utility classes
    Object.entries(palette).forEach(([shade, color]) => {
        css += `.bg-${colorCode}-${shade} { background-color: ${color}; }\n`;
        css += `.text-${colorCode}-${shade} { color: ${color}; }\n`;
        css += `.border-${colorCode}-${shade} { border-color: ${color}; }\n`;
        css += `.ring-${colorCode}-${shade} { --tw-ring-color: ${color}; }\n`;
    });
    
    return css;
});

onMounted(() => {
    gsap.from(heroTitle.value, { y: '20vh', opacity: 0, duration: 1.5, ease: 'power2.out' })
    gsap.from(heroSubtitle.value, { y: '30vh', opacity: 0, duration: 2, ease: 'power2.out', delay: 0.5 })

    // Check if showcase is already in viewport
    if (isInViewport(showcaseStartAni.value)) { // Apply immediate animation like hero elements if in viewport
      gsap.from(showcaseImages.value, { y: '10vh', opacity: 0, duration: 2, ease: 'power2.in', delay: 0.45 });
    } else { // Apply scroll-based animation if not in viewport
      gsap.fromTo(showcaseImages.value, { y: '20vh', opacity: 0 }, { y: 0, opacity: 1, duration: 2, ease: 'power2.out', scrollTrigger: { trigger: showcaseStartAni.value, start: 'top 75%', end: 'top 25%', scrub: true, invalidateOnRefresh: true }});
    }

    // Bento Grid 1
    gsap.fromTo(bentoGridShowcase1Ref.value, { y: '15vh', opacity: 0 }, { y: 0, opacity: 1, duration: 2, ease: 'power2.out', scrollTrigger: { trigger: bentoGrid1StartAni.value, start: 'top 85%', end: 'top 35%', scrub: true, invalidateOnRefresh: true }});
    gsap.fromTo(bentoGridInfotext1Ref.value, { y: '15vh', opacity: 0 }, { y: 0, opacity: 1, duration: 2, ease: 'power2.out', scrollTrigger: { trigger: bentoGrid1StartAni.value, start: 'top 65%', end: 'top 45%', scrub: true, invalidateOnRefresh: true }});
    // Bento Grid 2
    gsap.fromTo(bentoGridShowcase2Ref.value, { y: '15vh', opacity: 0 }, { y: 0, opacity: 1, duration: 2, ease: 'power2.out', scrollTrigger: { trigger: bentoGrid2StartAni.value, start: 'top 85%', end: 'top 35%', scrub: true, invalidateOnRefresh: true }});
    gsap.fromTo(bentoGridInfotext2Ref.value, { y: '15vh', opacity: 0 }, { y: 0, opacity: 1, duration: 2, ease: 'power2.out', scrollTrigger: { trigger: bentoGrid2StartAni.value, start: 'top 65%', end: 'top 45%', scrub: true, invalidateOnRefresh: true }});
    // Bento Grid 3
    gsap.fromTo(bentoGridShowcase3Ref.value, { y: '15vh', opacity: 0 }, { y: 0, opacity: 1, duration: 2, ease: 'power2.out', scrollTrigger: { trigger: bentoGrid3StartAni.value, start: 'top 65%', end: 'top 25%', scrub: true, invalidateOnRefresh: true }});    
    gsap.fromTo(bentoGridInfotext3Ref.value, { y: '15vh', opacity: 0 }, { y: 0, opacity: 1, duration: 2, ease: 'power2.out', scrollTrigger: { trigger: bentoGrid3StartAni.value, start: 'top 85%', end: 'top 65%', scrub: true, invalidateOnRefresh: true }});
    // Bento Grid 4
    gsap.fromTo(bentoGridShowcase4Ref.value, { y: '15vh', opacity: 0 }, { y: 0, opacity: 1, duration: 2, ease: 'power2.out', scrollTrigger: { trigger: bentoGrid4StartAni.value, start: 'top 65%', end: 'top 25%', scrub: true, invalidateOnRefresh: true }});    
    gsap.fromTo(bentoGridInfotext4Ref.value, { y: '15vh', opacity: 0 }, { y: 0, opacity: 1, duration: 2, ease: 'power2.out', scrollTrigger: { trigger: bentoGrid4StartAni.value, start: 'top 85%', end: 'top 65%', scrub: true, invalidateOnRefresh: true }});

    // Info Section - Enhanced with staggered animations
    gsap.fromTo(subDescription.value, { y: 50, opacity: 0 }, { y: 0, opacity: 1, duration: 1, ease: 'power2.out', scrollTrigger: { trigger: infoStartAni.value, start: 'top 75%', toggleActions: "play none none none" }});
    
    gsap.fromTo(extraInfo.value, { y: 60, opacity: 0 }, { y: 0, opacity: 1, duration: 1, delay: 0.2, ease: 'power2.out', scrollTrigger: { trigger: infoStartAni.value, start: 'top 70%', toggleActions: "play none none none" }});
    gsap.fromTo(infoImage.value, { y: 80, opacity: 0 }, { y: 0, opacity: 1, duration: 1, delay: 0.4, ease: 'power2.out', scrollTrigger: { trigger: infoStartAni.value, start: 'top 65%', toggleActions: "play none none none" }});
})

</script>

<template>
    <!-- Dynamic styles for project colors -->
    <component :is="'style'" v-if="project?.project_code && project?.main_color">
        {{ projectColorStyles }}
    </component>

    <div>


        <div class="absolute opacity-35 inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:54px_54px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

        <div class="wide-container mt-16 sm:mt-20 md:mt-28 flex flex-col items-center">
            <div ref="heroLogo">
                <img :src="'/storage/'+project.logo" 
                    :alt="`${project.title} logo`" 
                    class="h-32 md:h-40 w-auto object-contain mx-auto"
                    :style="`filter: drop-shadow(0 0 10px ${project.main_color}); fill: ${project.main_color}`" />
            </div>
            <h2 ref="heroTitle" class="text-5xl sm:text-6xl lg:text-8xl font-medium text-center text-zinc-900 max-w-4xl tracking-tight leading-[120%]">
                <span :class="'text-'+project.project_code+'-500'">{{ project.subtitle }}</span>
            </h2>
            <p ref="heroSubtitle" class="text-xl sm:font-medium sm:text-center text-zinc-900 max-w-6xl whitespace-pre-line">{{ project.long_description }}</p>
        </div>

        <div class="wide-container flex flex-col items-center">
            <div class="mx-auto max-w-7xl px-6 lg:flex lg:px-8">
                <div class="mt-10 grid grid-cols-1 gap-4 sm:mt-16 lg:grid-cols-6 lg:grid-rows-2">
                    <div ref="bentoGrid1StartAni" class="flex p-px lg:col-span-4">
                        <div :class="[`overflow-hidden rounded-lg bg-${project.project_code}-200/65 ring-1 ring-white/15 max-lg:rounded-t-[2rem] lg:rounded-tl-[2rem] w-full`]">
                            <div ref="bentoGridShowcase1Ref" class="relative h-80 flex items-center justify-center p-10 bg-gradient-radial select-none">
                                <div class="absolute inset-x-10 bottom-0 overflow-hidden rounded-b-3xl bg-zinc-900/5 p-2 ring-1 ring-zinc-900/10 ring-inset shadow-2xl">
                                    <img :src="project.bento_grid_top_left_image ? '/storage/' + project.bento_grid_top_left_image : 'https://tailwindcss.com/plus-assets/img/component-images/project-app-screenshot.png'" 
                                         :alt="project.bento_grid_top_left_image_alt || 'App screenshot'" 
                                         width="2432" height="1442" 
                                         class="w-[76rem] rounded-b-2xl shadow-2xl ring-1 ring-zinc-900/10" />
                                </div>
                            </div>
                            <div ref="bentoGridInfotext1Ref" class="p-4 sm:p-6 md:p-10 pb-8">
                                <h3 class="text-lg font-medium text-zinc-900/70">{{ project.bento_grid_top_left_title }}</h3>
                                <p class="mt-2 text-2xl font-medium tracking-tight text-zinc-900">{{ project.bento_grid_top_left_heading }}</p>
                                <p class="mt-2 max-w-2xl text-lg text-zinc-900/80 whitespace-pre-line">{{ project.bento_grid_top_left_description }}</p>
                            </div>
                        </div>
                    </div>
                    <div ref="bentoGrid2StartAni" class="flex p-px lg:col-span-2">
                        <div :class="[`overflow-hidden rounded-lg bg-${project.project_code}-200/65 ring-1 ring-white/15 lg:rounded-tr-[2rem] w-full flex flex-col justify-between`]">
                            <div ref="bentoGridShowcase2Ref" class="h-80 w-full flex flex-col space-y-16 py-10 select-none">
                                <div :class="[`relative flex items-center justify-start border-b-2 border-dotted border-${project.project_code}-400`]" v-if="project.integrations && project.integrations[0]">
                                    <div :class="[`absolute left-10 flex items-center bg-${project.project_code}-200 ring-1 ring-${project.project_code}-400 rounded-full px-4 py-2`]">
                                        <img :src="'/storage/' + project.integrations[0].logo" :alt="`${project.integrations[0].name} Icon`" class="h-5 w-5 mr-2" />
                                        <p :class="[`text-${project.project_code}-600 text-nowrap text-sm font-medium`]">{{ project.integrations[0].name }}</p>
                                    </div>
                                </div>
                                <div :class="[`relative flex items-center justify-start border-b-2 border-dotted border-${project.project_code}-400`]" v-if="project.integrations && project.integrations[1]">
                                    <div :class="[`absolute left-48 flex items-center bg-${project.project_code}-200 ring-1 ring-${project.project_code}-400 rounded-full px-4 py-2`]">
                                        <img :src="'/storage/' + project.integrations[1].logo" :alt="`${project.integrations[1].name} Icon`" class="h-5 w-5 mr-2" />
                                        <p :class="[`text-${project.project_code}-600 text-nowrap text-sm font-medium`]">{{ project.integrations[1].name }}</p>
                                    </div>
                                </div>
                                <div :class="[`relative flex items-center justify-start border-b-2 border-dotted border-${project.project_code}-400`]" v-if="project.integrations && project.integrations[2]">
                                    <div :class="[`absolute left-24 flex items-center bg-${project.project_code}-200 ring-1 ring-${project.project_code}-400 rounded-full px-4 py-2`]">
                                        <img :src="'/storage/' + project.integrations[2].logo" :alt="`${project.integrations[2].name} Icon`" class="h-5 w-5 mr-2" />
                                        <p :class="[`text-${project.project_code}-600 text-nowrap text-sm font-medium`]">{{ project.integrations[2].name }}</p>
                                    </div>
                                </div>
                                <div :class="[`relative flex items-center justify-start border-b-2 border-dotted border-${project.project_code}-400`]" v-if="project.integrations && project.integrations[3]">
                                    <div :class="[`absolute left-60 flex items-center bg-${project.project_code}-200 ring-1 ring-${project.project_code}-400 rounded-full px-4 py-2`]">
                                        <img :src="'/storage/' + project.integrations[3].logo" :alt="`${project.integrations[3].name} Icon`" class="h-5 w-5 mr-2" />
                                        <p :class="[`text-${project.project_code}-600 text-nowrap text-sm font-medium`]">{{ project.integrations[3].name }}</p>
                                    </div>
                                </div>
                                <div :class="[`relative flex items-center justify-start border-b-2 border-dotted border-${project.project_code}-400`]" v-if="project.integrations && project.integrations[4]">
                                    <div :class="[`absolute left-20 flex items-center bg-${project.project_code}-200 ring-1 ring-${project.project_code}-400 rounded-full px-4 py-2`]">
                                        <img :src="'/storage/' + project.integrations[4].logo" :alt="`${project.integrations[4].name} Icon`" class="h-5 w-5 mr-2" />
                                        <p :class="[`text-${project.project_code}-600 text-nowrap text-sm font-medium`]">{{ project.integrations[4].name }}</p>
                                    </div>
                                </div>
                            </div>
                            <div ref="bentoGridInfotext2Ref" class="p-4 sm:p-6 md:p-10 pb-8">
                                <h3 class="text-lg font-medium text-zinc-900/70">{{ project.bento_grid_top_right_title }}</h3>
                                <p class="mt-2 text-2xl font-medium tracking-tight text-zinc-900">{{ project.bento_grid_top_right_heading }}</p>
                                <p class="mt-2 max-w-2xl text-lg text-zinc-900/80 whitespace-pre-line">{{ project.bento_grid_top_right_description }}</p>
                            </div>
                        </div>
                    </div>
                    <div ref="bentoGrid3StartAni" class="flex p-px lg:col-span-2">
                        <div :class="[`overflow-hidden rounded-lg bg-${project.project_code}-200/65 ring-1 ring-white/15 lg:rounded-bl-[2rem] w-full flex flex-col justify-between`]">
                            <div ref="bentoGridInfotext3Ref" class="p-4 sm:p-6 md:p-10 pb-8">
                                <h3 class="text-lg font-medium text-zinc-900/70">{{ project.bento_grid_bottom_left_title }}</h3>
                                <p class="mt-2 text-2xl font-medium tracking-tight text-zinc-900">{{ project.bento_grid_bottom_left_heading }}</p>
                                <p class="mt-2 max-w-2xl text-lg text-zinc-900/80 whitespace-pre-line">{{ project.bento_grid_bottom_left_description }}</p>
                            </div>
                            <div ref="bentoGridShowcase3Ref" class="relative h-80 flex bg-gradient-radial select-none">
                                <div class="mx-10 w-full rounded-t-3xl border-x-8 border-t-8 border-zinc-700 bg-zinc-900 shadow-2xl overflow-hidden">
                                    <img class="object-top w-full h-auto object-cover" 
                                         :src="project.bento_grid_bottom_left_image ? '/storage/' + project.bento_grid_bottom_left_image : 'https://tailwindcss.com/plus-assets/img/component-images/bento-03-mobile-friendly.png'" 
                                         :alt="project.bento_grid_bottom_left_image_alt || 'Mobile screenshot'" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div ref="bentoGrid4StartAni" class="flex p-px lg:col-span-4">
                        <div :class="[`overflow-hidden rounded-lg bg-${project.project_code}-200/65 ring-1 ring-white/15 max-lg:rounded-b-[2rem] lg:rounded-br-[2rem] w-full flex flex-col justify-between`]">
                            <div ref="bentoGridInfotext4Ref" class="p-4 sm:p-6 md:p-10 pb-8">
                                <h3 class="text-lg font-medium text-zinc-900/70">{{ project.bento_grid_bottom_right_title }}</h3>
                                <p class="mt-2 text-2xl font-medium tracking-tight text-zinc-900">{{ project.bento_grid_bottom_right_heading }}</p>
                                <p class="mt-2 max-w-2xl text-lg text-zinc-900/80 whitespace-pre-line">{{ project.bento_grid_bottom_right_description }}</p>
                            </div>
                            <div ref="bentoGridShowcase4Ref" class="relative h-80 flex bg-gradient-radial select-none">
                                <div class="mx-10 w-full rounded-t-3xl bg-zinc-900/5 p-2 ring-1 ring-zinc-900/10 ring-inset shadow-2xl overflow-hidden">
                                    <img :src="project.bento_grid_bottom_right_image ? '/storage/' + project.bento_grid_bottom_right_image : 'https://tailwindcss.com/plus-assets/img/component-images/project-app-screenshot.png'" 
                                         :alt="project.bento_grid_bottom_right_image_alt || 'App screenshot'" 
                                         width="2432" height="1442" 
                                         class="w-full rounded-t-2xl shadow-2xl ring-1 ring-zinc-900/10" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div ref="showcaseStartAni" class="wide-container flex flex-col items-center -mb-24">
            <div class="h-[52rem]">
                <div ref="showcaseImages" class="group flex max-md:flex-col justify-center gap-2 w-full mx-auto mb-10 min-h-72 h-5/6">
                    <!-- Left Showcase -->
                    <article v-if="project.showcase_left" class="group/article relative w-full rounded-xl overflow-hidden md:group-hover:[&:not(:hover)]:w-[20%] md:group-focus-within:[&:not(:focus-within):not(:hover)]:w-[20%] transition-all duration-500 ease-[cubic-bezier(.5,.85,.25,1.15)] before:absolute before:inset-x-0 before:bottom-0 before:h-2/3 before:bg-gradient-to-t before:from-black/90 before:transition-opacity md:before:opacity-0 md:hover:before:opacity-100 focus-within:before:opacity-100 after:opacity-0 md:group-hover:[&:not(:hover)]:after:opacity-100 md:group-focus-within:[&:not(:focus-within):not(:hover)]:after:opacity-100 after:absolute after:inset-0 after:bg-white/30 after:backdrop-blur after:rounded-lg after:transition-all">
                        <div class="absolute inset-0 text-white z-10 p-3 flex flex-col justify-end select-none">
                            <h1 class="text-xl font-medium md:whitespace-nowrap md:truncate md:opacity-0 group-hover/article:opacity-100 group-focus-within/article:opacity-100 md:translate-y-2 group-hover/article:translate-y-0 group-focus-within/article:translate-y-0 transition duration-500 ease-smooth group-hover/article:delay-300 group-focus-within/article:delay-300">
                                {{ project.showcase_left_title }}
                            </h1>
                            <span class="text-3xl font-medium md:whitespace-nowrap md:truncate md:opacity-0 group-hover/article:opacity-100 group-focus-within/article:opacity-100 md:translate-y-2 group-hover/article:translate-y-0 group-focus-within/article:translate-y-0 transition duration-500 ease-smooth group-hover/article:delay-500 group-focus-within/article:delay-500">
                                {{ project.showcase_left_description }}
                            </span>
                        </div>
                        <img class="object-cover h-full w-full" :src="'/storage/' + project.showcase_left" :alt="project.showcase_left_alt || `${project.showcase_left_title} - ${project.showcase_left_description}`" />
                    </article>

                    <!-- Center Showcase -->
                    <article v-if="project.showcase_center" class="group/article relative w-full rounded-xl overflow-hidden md:group-hover:[&:not(:hover)]:w-[20%] md:group-focus-within:[&:not(:focus-within):not(:hover)]:w-[20%] transition-all duration-500 ease-[cubic-bezier(.5,.85,.25,1.15)] before:absolute before:inset-x-0 before:bottom-0 before:h-2/3 before:bg-gradient-to-t before:from-black/90 before:transition-opacity md:before:opacity-0 md:hover:before:opacity-100 focus-within:before:opacity-100 after:opacity-0 md:group-hover:[&:not(:hover)]:after:opacity-100 md:group-focus-within:[&:not(:focus-within):not(:hover)]:after:opacity-100 after:absolute after:inset-0 after:bg-white/30 after:backdrop-blur after:rounded-lg after:transition-all">
                        <div class="absolute inset-0 text-white z-10 p-3 flex flex-col justify-end select-none">
                            <h1 class="text-xl font-medium md:whitespace-nowrap md:truncate md:opacity-0 group-hover/article:opacity-100 group-focus-within/article:opacity-100 md:translate-y-2 group-hover/article:translate-y-0 group-focus-within/article:translate-y-0 transition duration-500 ease-smooth group-hover/article:delay-300 group-focus-within/article:delay-300">
                                {{ project.showcase_center_title }}
                            </h1>
                            <span class="text-3xl font-medium md:whitespace-nowrap md:truncate md:opacity-0 group-hover/article:opacity-100 group-focus-within/article:opacity-100 md:translate-y-2 group-hover/article:translate-y-0 group-focus-within/article:translate-y-0 transition duration-500 ease-smooth group-hover/article:delay-500 group-focus-within/article:delay-500">
                                {{ project.showcase_center_description }}
                            </span>
                        </div>
                        <img class="object-cover h-full w-full" :src="'/storage/' + project.showcase_center" :alt="project.showcase_center_alt || `${project.showcase_center_title} - ${project.showcase_center_description}`" />
                    </article>

                    <!-- Right Showcase -->
                    <article v-if="project.showcase_right" class="group/article relative w-full rounded-xl overflow-hidden md:group-hover:[&:not(:hover)]:w-[20%] md:group-focus-within:[&:not(:focus-within):not(:hover)]:w-[20%] transition-all duration-500 ease-[cubic-bezier(.5,.85,.25,1.15)] before:absolute before:inset-x-0 before:bottom-0 before:h-2/3 before:bg-gradient-to-t before:from-black/90 before:transition-opacity md:before:opacity-0 md:hover:before:opacity-100 focus-within:before:opacity-100 after:opacity-0 md:group-hover:[&:not(:hover)]:after:opacity-100 md:group-focus-within:[&:not(:focus-within):not(:hover)]:after:opacity-100 after:absolute after:inset-0 after:bg-white/30 after:backdrop-blur after:rounded-lg after:transition-all">
                        <div class="absolute inset-0 text-white z-10 p-3 flex flex-col justify-end select-none">
                            <h1 class="text-xl font-medium md:whitespace-nowrap md:truncate md:opacity-0 group-hover/article:opacity-100 group-focus-within/article:opacity-100 md:translate-y-2 group-hover/article:translate-y-0 group-focus-within/article:translate-y-0 transition duration-500 ease-smooth group-hover/article:delay-300 group-focus-within/article:delay-300">
                                {{ project.showcase_right_title }}
                            </h1>
                            <span class="text-3xl font-medium md:whitespace-nowrap md:truncate md:opacity-0 group-hover/article:opacity-100 group-focus-within/article:opacity-100 md:translate-y-2 group-hover/article:translate-y-0 group-focus-within/article:translate-y-0 transition duration-500 ease-smooth group-hover/article:delay-500 group-focus-within/article:delay-500">
                                {{ project.showcase_right_description }}
                            </span>
                        </div>
                        <img class="object-cover h-full w-full" :src="'/storage/' + project.showcase_right" :alt="project.showcase_right_alt || `${project.showcase_right_title} - ${project.showcase_right_description}`" />
                    </article>
                </div>
            </div>
        </div>

        <div ref="infoStartAni" class="wide-container flex flex-col items-center">
            <h2 ref="subDescription" class="text-4xl sm:text-5xl lg:text-7xl font-medium text-zinc-900 tracking-tight leading-[120%]">{{ project.sub_description }}</h2>
            <div class="flex flex-col lg:flex-row lg:items-center gap-16 mt-24 max-w-7xl mx-auto">
                <div class="lg:w-2/5">
                    <p ref="extraInfo" class="text-2xl font-medium text-zinc-900">{{ project.extra_info }}</p>
                </div>
                <div ref="infoImage" class="flex lg:w-3/5">
                    <div class="rounded-xl my-auto h-auto bg-zinc-900/5 p-2 ring-1 ring-zinc-900/10 ring-inset lg:rounded-2xl lg:p-4">
                        <img :src="'https://tailwindcss.com/plus-assets/img/component-images/project-app-screenshot.png'" alt="App screenshot" class="w-full object-scale-down rounded-md shadow-2xl ring-1 ring-zinc-900/10" />
                    </div>
                </div>
            </div>
        </div>
    
        <!-- Software Solutions Section -->
        <div ref="midStartAnimation" id="software-solutions" class="bg-white">
            <div class="wide-container">
                <!-- Main content grid -->
                <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
                    <!-- Left Column - Text content with specs -->
                    <div class="lg:col-span-7 flex flex-col">
                        <hgroup>
                            <h2 class="text-4xl sm:text-5xl font-bold mb-4">Fast Building, Long Lasting</h2>
                            <p class="text-lg text-zinc-700 mb-16">
                                We maken slimme oplossingen die processen versnellen, systemen verbinden en data overzichtelijk maken. Geen poespas. Geen ruis. Gewoon technologie die klopt en meebeweegt.
                            </p>
                        </hgroup>
                        <!-- Feature Grid - 2x2 layout -->
                        <div ref="midTextRef" class="grid grid-cols-2 gap-6 mb-8">
                            <!-- Feature 1: Speed -->
                            <div>
                                <p class="text-balance">
                                    <span class="text-orange-500 text-4xl sm:text-5xl font-bold block">< 2.5 sec</span>
                                    <span class="text-xl font-medium text-zinc-600">Gemiddelde laadtijd</span>
                                </p>
                            </div>

                            <!-- Feature 2: Implementation Time -->
                            <div>
                                <p class="text-balance">
                                    <span class="text-orange-500 text-4xl sm:text-5xl font-bold block">65%</span>
                                    <span class="text-xl font-medium text-zinc-600">minder werkdruk</span>
                                </p>
                            </div>

                            <!-- Feature 3: Stability -->
                            <div>
                                <p class="text-balance">
                                    <span class="text-orange-500 text-4xl sm:text-5xl font-bold block">99.9%</span>
                                    <span class="text-xl font-medium text-zinc-600">uptime garantie</span>
                                </p>
                            </div>

                            <!-- Feature 4: Integrations -->
                            <div>
                                <p class="text-balance">
                                    <span class="text-orange-500 text-4xl sm:text-5xl font-bold block">3.8X</span>
                                    <span class="text-xl font-medium text-zinc-600">Hogere ROI</span>
                                </p>
                            </div>
                        </div>
                        
                        <p class="text-xs text-zinc-500 mt-1">* Vergeleken met traditionele software</p>

                        <!-- CTA Buttons -->
                        <div class="flex flex-col sm:flex-row gap-4 mt-8">
                            <Button href="/#client-testimonials" variant="gradient" icon="calendar-alt" class="group">
                                Plan een gesprek
                            </Button>
                            <Button href="/contact" variant="white" icon="paper-plane" class="group">
                                Stuur ons een bericht
                            </Button>
                        </div>
                    </div>
                    
                    <!-- Right Column - Product Showcase -->
                    <div class="lg:col-span-5 flex items-center justify-center relative">
                        <!-- Product Image with connector styled like the DJI image -->
                        <div class="relative w-full h-[120%] -mt-[10%]">
                            <!-- Placeholder for the product image -->
                            <img src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80" 
                                alt="Platform Solution" 
                                class="rounded-lg shadow-xl z-10 relative mx-auto h-full object-cover" />
                            
                            <!-- Green "connector" element mimicking the DJI charging connector -->
                            <div class="absolute bottom-0 right-1/4 w-8 h-16 bg-gradient-to-t from-green-400 to-green-500 rounded-b-md shadow-lg"></div>
                            
                            <!-- Screen overlay showing "80% Battery" like in the DJI image -->
                            <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black/70 w-32 h-32 rounded-lg flex flex-col items-center justify-center">
                                <div class="w-16 h-16 rounded-full border-4 border-green-500 flex items-center justify-center mb-2">
                                    <span class="text-2xl font-bold text-green-500">80%</span>
                                </div>
                                <span class="text-white text-sm">Power Charging</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="wide-container">
            <div :class="`w-full relative sm:h-[52rem] sm:-mt-48 lg:-mt-12 p-4 rounded-2xl bg-${project.project_code}-200/65 sm:bg-transparent flex flex-col -space-y-24`">
                <div class="sm:absolute sm:left-1/2 -bottom-8 lg:bottom-6 xl:bottom-12 transform sm:-translate-x-1/2 h-full">
                    <img v-motion :initial="{ y: 15, opacity: 0 }" :visible="{ y: 0, opacity: 1, transition: { delay: 250, type: 'spring', stiffness: '50', damping: '12.5', duration: 1500 } }" class="h-full w-auto aspect-square object-scale-down" :src="product.media.mockupImages.mid.src" ::alt="`${product.content.title} | ${product.media.mockupImages.mid.alt}`" >
                </div>
                <div class="sm:absolute w-full top-72 bottom-0">
                    <img v-motion :initial="{ x: -15, y: 15, opacity: 0 }" :visible="{ x: 0, y: 0, opacity: 1, transition: { delay: 500, type: 'spring', stiffness: '50', damping: '12.5', duration: 1500 } }" class="h-full sm:w-2/5 aspect-square object-scale-down" :src="product.media.mockupImages.left.src" :alt="`${product.content.title} | ${product.media.mockupImages.left.alt}`" >
                </div>
                <div class="sm:absolute w-full top-64 bottom-0">
                    <img v-motion :initial="{ x: 15, y: 15, opacity: 0 }" :visible="{ x: 0, y: 0, opacity: 1, transition: { delay: 750, type: 'spring', stiffness: '50', damping: '12.5', duration: 1500 } }" class="h-full ml-auto mt-4 sm:w-2/5 aspect-square object-scale-down" :src="product.media.mockupImages.right.src" :alt="`${product.content.title} | ${product.media.mockupImages.right.alt}`" >
                </div>
            </div>
            <hgroup class="flex flex-col items-center text-center">
                <h2 class="text-4xl sm:text-5xl lg:text-7xl font-medium text-center text-zinc-900 tracking-tight leading-[120%]">{{ product.content.subDescription }}</h2>
                <p class="mt-12 text-2xl font-medium text-center text-zinc-900 max-w-6xl">{{ product.content.extraInfo }}</p>
            </hgroup>
        </div>
        
        <!-- Add testimonial section -->
        <div v-if="product.testimonial" class="wide-container py-16 mb-16">
            <div :class="`max-w-5xl mx-auto bg-gradient-to-r from-${project.project_code}-50/70 to-${project.project_code}-100/30 rounded-3xl overflow-hidden shadow-sm`">
                <div class="relative p-8 md:p-12 flex flex-col md:flex-row items-center md:items-start gap-8">
                    <!-- Decorative elements -->
                    <div class="absolute -top-10 -left-10 w-40 h-40 opacity-10">
                        <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-full h-full">
                            <path d="M40.8 15.2C40.8 6.80608 47.6061 0 56 0C64.3939 0 71.2 6.80608 71.2 15.2V24C71.2 32.3939 64.3939 39.2 56 39.2C47.6061 39.2 40.8 32.3939 40.8 24V15.2ZM0 15.2C0 6.80608 6.80608 0 15.2 0C23.5939 0 30.4 6.80608 30.4 15.2V24C30.4 32.3939 23.5939 39.2 15.2 39.2C6.80608 39.2 0 32.3939 0 24V15.2Z" :fill="`currentColor`" class="text-zinc-900"/>
                            <path d="M40.8 55.2C40.8 46.8061 47.6061 40 56 40C64.3939 40 71.2 46.8061 71.2 55.2V64C71.2 72.3939 64.3939 79.2 56 79.2C47.6061 79.2 40.8 72.3939 40.8 64V55.2ZM0 55.2C0 46.8061 6.80608 40 15.2 40C23.5939 40 30.4 46.8061 30.4 55.2V64C30.4 72.3939 23.5939 79.2 15.2 79.2C6.80608 79.2 0 72.3939 0 64V55.2Z" :fill="`currentColor`" class="text-zinc-900"/>
                        </svg>
                    </div>
                    
                    <!-- Logo or icon -->
                    <div 
                        :class="`flex-shrink-0 h-20 w-20 rounded-full md:rounded-xl p-1 ring-2 ring-${project.project_code}-300/50 flex items-center justify-center overflow-hidden bg-white`"
                        v-motion
                        :initial="{ scale: 0.8, opacity: 0 }"
                        :visible="{ scale: 1, opacity: 1, transition: { duration: 700 } }"
                    >
                        <img 
                            v-if="product.testimonial.logo" 
                            :src="product.testimonial.logo" 
                            :alt="`${product.meta.name} logo`" 
                            class="w-full h-full object-contain p-1"
                        />
                        <font-awesome-icon 
                            v-else 
                            :icon="['fas', product.meta.icon || 'building']" 
                            :class="`text-${project.project_code}-500 text-3xl`" 
                        />
                    </div>
                    
                    <!-- Content -->
                    <div class="flex-1">
                        <!-- Quote -->
                        <div 
                            class="relative text-xl md:text-2xl text-zinc-700 italic mb-6"
                            v-motion
                            :initial="{ y: 20, opacity: 0 }"
                            :visible="{ y: 0, opacity: 1, transition: { duration: 700, delay: 200 } }"
                        >
                            <div :class="`absolute -left-3 -top-3 text-${project.project_code}-400 text-4xl opacity-50`">"</div>
                            <p class="relative">{{ product.testimonial.quote }}</p>
                            <div :class="`absolute -right-1 bottom-0 text-${project.project_code}-400 text-4xl opacity-50`">"</div>
                        </div>
                        
                        <!-- Author info -->
                        <div 
                            class="flex items-center"
                            v-motion
                            :initial="{ y: 20, opacity: 0 }"
                            :visible="{ y: 0, opacity: 1, transition: { duration: 700, delay: 400 } }"
                        >
                            <div>
                                <p :class="`font-semibold text-${project.project_code}-700`">{{ product.testimonial.author }}</p>
                                <p class="text-zinc-600 text-sm flex items-center">
                                    {{ product.testimonial.role }}
                                    <span class="mx-2 text-zinc-300 text-xs">•</span>
                                    {{ product.testimonial.project || product.content.subTitle }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
    </div>
</template>

<style scoped>
/* Ensure SVG takes full height */
:deep(svg) {
    height: 100%;
    width: auto;
}

/* Target paths and shapes in the SVG */
:deep(svg path),
:deep(svg rect),
:deep(svg circle),
:deep(svg polygon),
:deep(svg g) {
    fill: currentColor;
    transition: fill 0.3s ease;
}
</style>